import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  MoreHorizontal, 
  Plus, 
  Heart, 
  Activity, 
  Sparkles, 
  Calendar,
  User,
  Users,
  MessageSquare,
  Info,
  LogOut,
  Shield
} from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useAdminCheck } from '@/hooks/useAdminCheck';
import { useNavigationState } from '@/hooks/useNavigationState';
import { authService } from '@/services/auth';
import { isMobileDevice } from '@/lib/utils';
import { useHaptics } from '@/hooks/useHaptics';
import { SimpleBottomSheet } from '@/components/ui/SimpleBottomSheet';

interface MoreMenuProps {
  variant?: 'mobile' | 'desktop';
}

const MoreMenu: React.FC<MoreMenuProps> = ({ variant = 'desktop' }) => {
  const { user } = useUser();
  const { profile } = useUserProfile();
  const { isAdmin } = useAdminCheck();
  const { shouldShowInMoreMenu } = useNavigationState();
  const navigate = useNavigate();
  const { lightImpact } = useHaptics();

  const [modalOpen, setModalOpen] = React.useState(false);

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  const handleLogout = async () => {
    lightImpact();
    closeModal();
    await authService.signOut();
  };

  const getDisplayName = () => {
    return profile?.first_name || 'User';
  };

  const handleNavigate = (path: string) => {
    lightImpact();
    closeModal();
    navigate(path);
  };

  const handleMenuClick = () => {
    if (isMobileDevice()) {
      lightImpact();
      openModal();
    }
  };

  // Menu items configuration
  const menuSections = [
    {
      items: [
        { key: 'add-recipes', icon: Plus, label: 'Add Recipes', path: '/add-recipes', show: true },
        { key: 'family-activity', icon: Activity, label: 'Family Activity', path: '/family-activity', show: shouldShowInMoreMenu('family-activity') },
        { key: 'ai-suggestions', icon: Sparkles, label: 'AI Suggestions', path: '/meal-suggestions', show: shouldShowInMoreMenu('ai-suggestions') },
        { key: 'monthly-view', icon: Calendar, label: 'Monthly View', path: '/monthly-meal-view', show: shouldShowInMoreMenu('monthly-view') },
      ]
    },
    {
      items: [
        { key: 'profile', icon: User, label: 'Profile', path: '/profile', show: true },
        { key: 'family-circle', icon: Users, label: 'Family Circle', path: '/family-circle', show: true },
        { key: 'feedback', icon: MessageSquare, label: 'Feedback', path: '/share-feedback', show: true },
        { key: 'about', icon: Info, label: 'About', path: '/about', show: true },
      ]
    },
    {
      items: [
        { key: 'admin', icon: Shield, label: 'Admin Dashboard', path: '/admin-dashboard-xyz789', show: isAdmin },
        { key: 'logout', icon: LogOut, label: 'Sign out', path: '', show: true, isDestructive: true },
      ]
    }
  ];

  if (isMobileDevice()) {
    return (
      <>
        <Button 
          variant="ghost" 
          className="relative h-10 w-10 rounded-full bg-primary text-primary-foreground shadow-md self-center"
          onClick={handleMenuClick}
        >
          <MoreHorizontal className="h-5 w-5" />
        </Button>

        <SimpleBottomSheet
          isOpen={modalOpen}
          onClose={closeModal}
          title="Menu"
        >
          <div className="space-y-4">
            {/* User Info Header */}
            <div className="flex items-center space-x-4 p-4 bg-muted/50 rounded-xl">
              <div className="flex items-center justify-center h-12 w-12 rounded-full bg-sage text-white">
                <User className="h-6 w-6" />
              </div>
              <div className="flex flex-col min-w-0">
                <p className="text-lg font-semibold truncate">{getDisplayName()}</p>
                <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>

            {/* Menu Sections */}
            <div className="space-y-4">
              {menuSections.map((section, sectionIndex) => {
                const visibleItems = section.items.filter(item => item.show);
                if (visibleItems.length === 0) return null;

                return (
                  <div key={sectionIndex}>
                    {sectionIndex > 0 && <div className="border-t border-border my-4" />}
                    <div className="space-y-2">
                      {visibleItems.map((item) => (
                        <button
                          key={item.key}
                          onClick={() => item.key === 'logout' ? handleLogout() : handleNavigate(item.path)}
                          className={`w-full flex items-center space-x-4 p-3 rounded-xl transition-colors text-left ${
                            item.isDestructive 
                              ? 'hover:bg-destructive/10 text-destructive' 
                              : 'hover:bg-muted/50'
                          }`}
                        >
                          <item.icon className="h-5 w-5 flex-shrink-0" />
                          <span className="text-base">{item.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Bottom padding for safe scrolling */}
            <div className="h-4" />
          </div>
        </SimpleBottomSheet>
      </>
    );
  }

  // Web version - dropdown menu with icon button
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-8 w-8 rounded-full">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-sage text-white">
              <MoreHorizontal className="h-4 w-4" />
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        {/* User Info Header */}
        <div className="flex items-center gap-3 p-3 pb-2">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-sage text-white text-xs">
              {getDisplayName().charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col min-w-0 flex-1">
            <p className="text-sm font-medium truncate">{getDisplayName()}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
        </div>
        <DropdownMenuSeparator />
        
        {menuSections.map((section, sectionIndex) => {
          const visibleItems = section.items.filter(item => item.show);
          if (visibleItems.length === 0) return null;

          return (
            <div key={sectionIndex}>
              {sectionIndex > 0 && <DropdownMenuSeparator />}
              {visibleItems.map((item) => (
                <DropdownMenuItem
                  key={item.key}
                  onClick={() => item.key === 'logout' ? handleLogout() : navigate(item.path)}
                  className={item.isDestructive ? 'text-destructive' : ''}
                >
                  <item.icon className="mr-2 h-4 w-4" />
                  <span>{item.label}</span>
                </DropdownMenuItem>
              ))}
            </div>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default MoreMenu;
